"use client";

import { useMemo } from "react";
import "../styles/compass.css";

/* -------------------------------------------------------------------------- */
/* Configuration constants                                                      */
/* -------------------------------------------------------------------------- */

const ROWS = 32;
const COLS = 50;
const DOT_SPACING = 22; // px between each dot center
const RING_DELAY_MS = 110; // delay per unit of radial distance
const JITTER_MS = 220; // max per-dot jitter range (ms)

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

export interface LoaderProps {
  /** Status or progress message rendered below the spinner. */
  text: string;
  /** Optional className applied to the `.loader-page` root. */
  className?: string;
}

/* -------------------------------------------------------------------------- */
/* Dot grid builder                                                             */
/* -------------------------------------------------------------------------- */

type Dot = { x: number; y: number; delay: number };

/**
 * Deterministic pseudo-random jitter in [0, JITTER_MS) for a given dot index.
 *
 * Uses a stable trigonometric hash so the value is identical on the server
 * pre-render and the client hydration pass, preventing SSR/hydration mismatches.
 */
function dotJitter(index: number): number {
  const x = Math.sin(index * 127.1 + 311.7) * 43758.5453;
  return (x - Math.floor(x)) * JITTER_MS;
}

/**
 * Pre-compute a grid of {x, y, animationDelay} values for the dot-matrix
 * backdrop. Each dot's delay is proportional to its Euclidean distance from
 * the center plus a deterministic jitter, producing an organic radial wave.
 *
 * Built once per mount via `useMemo`.
 */
function buildDots(): Dot[] {
  const cx = (COLS - 1) / 2;
  const cy = (ROWS - 1) / 2;
  const list: Dot[] = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const dist = Math.hypot(c - cx, r - cy);
      const delay = dist * RING_DELAY_MS + dotJitter(r * COLS + c);
      list.push({ x: c * DOT_SPACING, y: r * DOT_SPACING, delay });
    }
  }
  return list;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Full-page radial-wave loading screen.
 *
 * Renders a 32×50 dot-matrix backdrop where each dot pulses outward in a
 * radiating wave from the center. The wave never resolves into perfect rings
 * because every dot has a random jitter on top of its distance-based delay.
 * A conic-gradient arc spinner and status text sit in front of the grid.
 *
 * Styles come from the shared `.loader-*` rules in `styles/compass.css`.
 */
export function Loader({ text, className }: LoaderProps) {
  const dots = useMemo(buildDots, []);
  const width = COLS * DOT_SPACING;
  const height = ROWS * DOT_SPACING;

  return (
    <div className={["loader-page", className].filter(Boolean).join(" ")}>
      {/* ── Dot grid backdrop ─────────────────────────────────────────── */}
      <div
        className="loader-dot-grid"
        style={{ width, height }}
        aria-hidden="true"
      >
        {dots.map((d, i) => (
          <span
            key={i}
            className="loader-dot"
            style={{
              left: d.x,
              top: d.y,
              animationDelay: `${d.delay.toFixed(0)}ms`,
            }}
          />
        ))}
      </div>

      {/* ── Spinner + text ────────────────────────────────────────────── */}
      <div className="loader-wrap">
        <div className="loader-spinner" aria-hidden="true" />
        <div className="loader-text" role="status" aria-live="polite">
          {text}
        </div>
      </div>
    </div>
  );
}
